<template>
  <div id="price-drops" class="align-start fill-height">
    <i-overlay
      v-if="loading && $vuetifyDisplay.smAndDown"
      text="Loading Price Drops..."
    />

    <v-row v-else class="g-container-large" no-gutters>
      <v-dialog
        v-if="specialDialog"
        v-model="specialDialog"
        max-width="900px"
        persistent
        @click:outside="reset"
        @keydown.esc="reset"
      >
        <i-edit-price-drops
          :family-groups="enrichedFamilyGroups"
          :pricing-regions="pricingRegions"
          :products="enrichedProducts"
          :special="editableSpecial"
          @cancel="reset"
          @save="reload"
        ></i-edit-price-drops>
      </v-dialog>

      <v-dialog
        v-if="confirmationDialog"
        v-model="confirmationDialog"
        width="400px"
        @click:outside="resetDeletion"
        @keydown.esc="resetDeletion"
      >
        <i-confirmation-dialog
          text="Confirm deletion of this Price Drop"
          action-text="Delete"
          :submitting="deletingSpecial"
          @cancel="resetDeletion"
          @confirm="confirmDeletion"
        />
      </v-dialog>

      <v-col cols="12">
        <h2 class="ma-0">{{ $route.meta.title }}</h2>

        <template>
          <v-row>
            <v-col cols="12" md="3" lg="2">
              <v-btn
                large
                rounded
                class="i-btn-block-secondary g-full-width"
                @click="editSpecial()"
              >
                Add Price Drop
              </v-btn>
            </v-col>
            <v-spacer></v-spacer>
            <v-col cols="12" md="3" lg="2">
              <i-instructions
                instructionKey="data-maintenance/price-drops"
              ></i-instructions>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="6" md="3" lg="2">
              <v-btn
                large
                class="i-btn-select g-full-width"
                :class="{ 'i-btn-select-active': selectedStatus === 'ACTIVE' }"
                @click="selectedStatus = 'ACTIVE'"
              >
                Active
              </v-btn>
            </v-col>
            <v-col cols="6" md="3" lg="2">
              <v-btn
                large
                class="i-btn-select g-full-width"
                :class="{
                  'i-btn-select-active': selectedStatus === 'UPCOMING'
                }"
                @click="selectedStatus = 'UPCOMING'"
              >
                Upcoming
              </v-btn>
            </v-col>
            <v-col cols="6" md="3" lg="2" v-if="dataApprovalRequired">
              <v-btn
                large
                class="i-btn-select g-full-width"
                :class="{
                  'i-btn-select-active': selectedStatus === 'AWAITING_APPROVAL'
                }"
                @click="selectedStatus = 'AWAITING_APPROVAL'"
              >
                <v-badge
                  color="accent"
                  :content="priceDropsRequiringAmendmentsCount"
                  right
                  :value="priceDropsRequiringAmendmentsCount"
                  >Awaiting Approval</v-badge
                >
              </v-btn>
            </v-col>
            <v-col cols="6" md="3" lg="2">
              <v-btn
                large
                class="i-btn-select g-full-width"
                :class="{ 'i-btn-select-active': selectedStatus === 'PAST' }"
                @click="selectedStatus = 'PAST'"
              >
                Past
              </v-btn>
            </v-col>
            <v-col
              cols="12"
              lg="4"
              :offset-lg="dataApprovalRequired ? 0 : 2"
              class="pl-lg-8 promotion-table-actions"
            >
              <div class="promotion-table-actions__inner">
                <i-local-filter
                  :filterPlaceholder="'Search by Target Type, Target(s), Family Group, or Product'"
                  @filter="localFilter"
                />
                <i-table-refresh-button
                  :loading="loading"
                  @refresh="refreshCurrentTable"
                />
              </div>
            </v-col>
          </v-row>
        </template>

        <template>
          <v-row>
            <v-col cols="12">
              <template
                v-if="!loading && filteredSpecialsByStatus.length === 0"
              >
                <i-empty-results hideButton :title="emptyFilteredResultsText" />
              </template>

              <template v-else>
                <template v-if="$vuetifyDisplay.smAndDown">
                  <div
                    v-for="item in filteredSpecialsByStatus"
                    :key="item.promotion.id"
                    class="mt-0 pa-0"
                  >
                    <v-card class="rounded-2 px-2 py-1 mb-6">
                      <template v-if="selectedStatus === 'AWAITING_APPROVAL'">
                        <v-row dense no-gutters>
                          <v-col cols="12">
                            <div
                              v-if="
                                item.promotion.approval &&
                                item.promotion.approval.status ===
                                  'AWAITING_APPROVAL'
                              "
                              class="g-bold g-border-radius-s i-status-block-blue px-2 py-2 my-1 text-center"
                            >
                              STATUS: AWAITING APPROVAL
                            </div>
                            <div
                              v-if="
                                item.promotion.approval &&
                                item.promotion.approval.status === 'REJECTED'
                              "
                              class="g-bold g-border-radius-s i-status-block-orange px-2 py-2 my-1 text-center"
                            >
                              STATUS: AMENDMENTS REQUESTED
                            </div>
                          </v-col>
                        </v-row>
                      </template>
                      <v-row no-gutters>
                        <v-col cols="6" class="py-1">
                          <span
                            ><strong>{{ headers[0].text }}</strong></span
                          >
                        </v-col>
                        <v-col cols="6" class="py-1 pl-4">
                          <v-chip
                            v-if="item.promotion.targetBannerGroups"
                            small
                            color="accent"
                            ><strong>BANNER</strong></v-chip
                          >
                          <v-chip
                            v-else-if="item.promotion.targetMultiStoreOperators"
                            small
                            color="primary"
                            ><strong>MSO</strong></v-chip
                          >
                          <v-chip
                            v-else-if="item.promotion.targetStores"
                            small
                            color="secondary"
                            ><strong>{{
                              isILR ? 'CUSTOMER' : 'RETAILER'
                            }}</strong></v-chip
                          >
                          <v-chip v-else small color="secondary"
                            ><strong>NATIONAL</strong></v-chip
                          >
                        </v-col>
                      </v-row>

                      <v-divider class="my-2"></v-divider>

                      <v-row no-gutters>
                        <v-col cols="6" class="py-1">
                          <span
                            ><strong>{{ headers[1].text }}</strong></span
                          >
                        </v-col>
                        <v-col cols="6" class="py-1 pl-4">
                          <span v-if="item.promotion.targetBannerGroups">
                            <span
                              v-for="item in item.promotion.targetBannerGroups"
                              :key="item.bannerGroupId"
                            >
                              {{ item.bannerGroupName }}
                              <br />
                            </span>
                          </span>
                          <span
                            v-else-if="item.promotion.targetMultiStoreOperators"
                          >
                            <span
                              v-for="item in item.promotion
                                .targetMultiStoreOperators"
                              :key="item.multiStoreOperatorId"
                            >
                              {{ item.multiStoreOperatorName }}
                              <br />
                            </span>
                          </span>
                          <span v-else-if="item.promotion.targetStores">
                            <span
                              v-for="item in item.promotion.targetStores"
                              :key="item.storeId"
                            >
                              {{ item.storeName }}
                              <br />
                            </span>
                          </span>
                          <span v-else> - </span>
                        </v-col>
                        <v-col cols="6" class="py-1">
                          <span>{{ headers[2].text }}</span>
                        </v-col>
                        <v-col cols="6" class="py-1 pl-4">
                          <span v-if="item.specialType === 'FAMILY_GROUP'">
                            <v-icon
                              class="i-promo-type-icon i-promo-type-icon-family-group"
                              small
                            >
                              mdi-alpha-f-circle-outline
                            </v-icon>
                            <span
                              class="i-tooltip-outer-text"
                              v-tooltip="{
                                html: `familyGroupProductsToolTipContent-${item.promotion.id}`
                              }"
                            >
                              Family Group
                            </span>
                            <span
                              :id="`familyGroupProductsToolTipContent-${item.promotion.id}`"
                            >
                              <strong>Family Group</strong><br />
                              <span
                                v-for="productName in item.familyGroupProducts"
                                :key="productName"
                              >
                                {{ productName }}<br />
                              </span>
                            </span>
                          </span>

                          <span
                            v-else-if="item.specialType === 'SINGLE_PRODUCT'"
                          >
                            <v-icon
                              class="i-promo-type-icon i-promo-type-icon-single-product"
                              small
                            >
                              mdi-alpha-p-circle-outline
                            </v-icon>
                            Single Product
                          </span>
                        </v-col>
                        <v-col cols="6" class="py-1">
                          <span>{{ headers[3].text }}</span>
                        </v-col>
                        <v-col cols="6" class="py-1 pl-4">
                          <span>
                            <router-link
                              v-if="!item.familyGroupDeleted"
                              :to="getLinkRoute(item)"
                              class="i-text-link"
                              >{{ item.displayName }}
                            </router-link>
                            <span v-else>{{ item.displayName }} (DELETED)</span>
                          </span>
                        </v-col>
                        <v-col cols="6" class="py-1">
                          <span>{{ headers[4].text }}</span>
                        </v-col>
                        <v-col cols="6" class="py-1 pl-4">
                          <span>{{
                            $filters.formatDateTime12h(
                              item.promotion.startDateTime
                            )
                          }}</span>
                        </v-col>
                        <v-col cols="6" class="py-1">
                          <span>{{ headers[5].text }}</span>
                        </v-col>
                        <v-col cols="6" class="py-1 pl-4">
                          <span>{{
                            $filters.formatDateTime12h(
                              item.promotion.endDateTime
                            )
                          }}</span>
                        </v-col>
                      </v-row>

                      <template v-if="!(item.promotion.status === 'PAST')">
                        <v-divider class="my-2"></v-divider>

                        <v-row no-gutters>
                          <v-col cols="12" class="py-1">
                            <v-btn
                              class="i-btn-actions-blue g-full-width"
                              :disabled="
                                item.promotion.status === 'PAST' ||
                                item.familyGroupDeleted ||
                                item.promotion.hasPendingEndDateAmendment ||
                                (item.promotion.approval &&
                                  item.promotion.approval.status ===
                                    'AWAITING_APPROVAL')
                              "
                              @click="editSpecial(item)"
                            >
                              <span>Edit Price Drop</span>
                              <v-icon small class="ml-1"
                                >mdi-circle-edit-outline</v-icon
                              >
                            </v-btn>
                          </v-col>
                          <v-col cols="12" class="py-1">
                            <v-btn
                              class="i-btn-actions-red g-full-width"
                              :disabled="
                                item.promotion.status !== 'UPCOMING' &&
                                item.promotion.status !== 'AWAITING_APPROVAL'
                              "
                              @click="startDeletion(item.promotion.id)"
                            >
                              <span>Delete Price Drop</span>
                              <v-icon small class="ml-1"
                                >mdi-delete-outline</v-icon
                              >
                            </v-btn>
                          </v-col>
                        </v-row>
                      </template>
                    </v-card>
                  </div>
                </template>

                <template v-else>
                  <v-data-table
                    class="pt-2 elevation-1 i-data-table"
                    item-key="promotion.id"
                    show-expand
                    v-model:expanded="expandedSpecial"
                    :headers="headersByStatus"
                    :items="filteredSpecialsByStatus"
                    :items-per-page="-1"
                    :loading="loading"
                    :no-data-text="emptyFilteredResultsText"
                    :no-results-text="emptyFilteredResultsText"
                  >
                    <template #[`item.targetType`]="{ item }">
                      <v-chip
                        v-if="item.promotion.targetBannerGroups"
                        small
                        color="accent"
                        >BANNER</v-chip
                      >
                      <v-chip
                        v-else-if="item.promotion.targetMultiStoreOperators"
                        small
                        color="primary"
                        >MSO</v-chip
                      >
                      <v-chip
                        v-else-if="item.promotion.targetStores"
                        small
                        color="secondary"
                        >{{ isILR ? 'CUSTOMER' : 'RETAILER' }}</v-chip
                      >
                      <v-chip v-else small color="green">ALL ZONES</v-chip>
                    </template>

                    <template #[`item.targetValue`]="{ item }">
                      <span v-if="item.promotion.targetBannerGroups">
                        <span
                          v-for="item in item.promotion.targetBannerGroups"
                          :key="item.bannerGroupId"
                        >
                          {{ item.displayName }}
                          <br />
                        </span>
                      </span>
                      <span
                        v-else-if="item.promotion.targetMultiStoreOperators"
                      >
                        <span
                          v-for="item in item.promotion
                            .targetMultiStoreOperators"
                          :key="item.multiStoreOperatorId"
                        >
                          {{ item.displayName }}
                          <br />
                        </span>
                      </span>
                      <span v-else-if="item.promotion.targetStores">
                        <span
                          v-for="item in item.promotion.targetStores"
                          :key="item.storeId"
                        >
                          {{ item.displayName }}
                          <br />
                        </span>
                      </span>
                      <span v-else> - </span>
                    </template>

                    <template v-slot:item.supplierItemCodes="{ item }">
                      <span
                        v-if="
                          item.supplierItemCodes &&
                          item.supplierItemCodes.length
                        "
                      >
                        <span
                          v-for="(code, i) in item.supplierItemCodes"
                          :key="i"
                        >
                          {{ code }}<br />
                        </span>
                      </span>
                      <span v-else>-</span>
                    </template>

                    <template v-slot:[`item.specialType`]="{ item }">
                      <div v-if="item.specialType === 'FAMILY_GROUP'">
                        <v-icon
                          class="i-promo-type-icon i-promo-type-icon-family-group"
                          small
                          v-tooltip.left="{
                            html: `familyGroupProductsToolTipContent-${item.promotion.id}`
                          }"
                        >
                          mdi-alpha-f-circle-outline
                        </v-icon>
                        <span
                          :id="`familyGroupProductsToolTipContent-${item.promotion.id}`"
                        >
                          <strong>Family Group</strong><br />
                          <span
                            v-for="productName in item.familyGroupProducts"
                            :key="productName"
                          >
                            {{ productName }}<br />
                          </span>
                        </span>
                      </div>

                      <v-icon
                        v-if="item.specialType === 'SINGLE_PRODUCT'"
                        class="i-promo-type-icon i-promo-type-icon-single-product"
                        small
                        v-tooltip.left="'Single Product'"
                      >
                        mdi-alpha-p-circle-outline
                      </v-icon>
                    </template>

                    <template #[`item.displayName`]="{ item }">
                      <router-link
                        v-if="!item.familyGroupDeleted"
                        :to="getLinkRoute(item)"
                        class="i-text-link"
                        >{{ item.displayName }}
                      </router-link>
                      <span v-else>{{ item.displayName }} (DELETED)</span>
                    </template>

                    <template #[`item.promotion.startDate`]="{ item }">
                      <span>{{
                        $filters.formatDateTime12h(item.promotion.startDateTime)
                      }}</span>
                    </template>

                    <template #[`item.promotion.endDate`]="{ item }">
                      <span>{{
                        $filters.formatDateTime12h(item.promotion.endDateTime)
                      }}</span>
                    </template>

                    <template #[`item.promotion.approvalStatus`]="{ item }">
                      <template v-if="item.promotion">
                        <p
                          v-if="
                            item.promotion.approval.status ===
                            'AWAITING_APPROVAL'
                          "
                          class="g-bold g-border-radius-s i-status-block-blue px-2 py-2 my-1 text-center"
                          v-resize-text="{
                            minFontSize: '13px',
                            maxFontSize: '15px'
                          }"
                        >
                          AWAITING APPROVAL
                        </p>
                        <p
                          v-if="item.promotion.approval.status === 'REJECTED'"
                          class="g-bold g-border-radius-s i-status-block-orange px-2 py-2 my-1 text-center"
                          v-resize-text="{
                            minFontSize: '13px',
                            maxFontSize: '15px'
                          }"
                        >
                          AMENDMENTS REQUESTED
                        </p>
                      </template>
                    </template>

                    <template #[`item.actions`]="{ item }">
                      <v-btn
                        color="secondary"
                        :disabled="
                          item.promotion.status === 'PAST' ||
                          item.familyGroupDeleted ||
                          item.promotion.hasPendingEndDateAmendment ||
                          (item.promotion.approval &&
                            item.promotion.approval.status ===
                              'AWAITING_APPROVAL')
                        "
                        icon
                        v-tooltip="{
                          content: 'Edit',
                          visible: $vuetifyDisplay.mdAndDown ? false : undefined
                        }"
                        @click="editSpecial(item)"
                      >
                        <v-icon>mdi-circle-edit-outline</v-icon>
                      </v-btn>

                      <v-btn
                        color="error"
                        :disabled="
                          item.promotion.status !== 'UPCOMING' &&
                          item.promotion.status !== 'AWAITING_APPROVAL'
                        "
                        icon
                        v-tooltip="{
                          content: 'Delete',
                          visible: $vuetifyDisplay.mdAndDown ? false : undefined
                        }"
                        @click="startDeletion(item.promotion.id)"
                      >
                        <v-icon>mdi-delete-outline</v-icon>
                      </v-btn>
                    </template>

                    <template v-slot:expanded-item="{ headers, item }">
                      <td :colspan="headers.length">
                        <v-simple-table
                          v-if="item.regionalPriceMap.length > 0"
                          class="my-5"
                          light
                        >
                          <template v-slot:default>
                            <thead>
                              <tr>
                                <th class="text-center pb-3 text-uppercase">
                                  Zones
                                </th>

                                <th class="text-center pb-3 text-uppercase">
                                  {{
                                    isServiceProduct(item)
                                      ? 'Price (ex Tax)'
                                      : 'Carton Price (ex Tax)'
                                  }}
                                </th>

                                <th class="text-center pb-3 text-uppercase">
                                  {{
                                    isServiceProduct(item)
                                      ? 'Amount Off (ex Tax)'
                                      : 'Amount off Carton (ex Tax)'
                                  }}
                                </th>

                                <th class="text-center pb-3 text-uppercase">
                                  <template v-if="isVariableWeight(item)"
                                    >Revised Cost / KG (ex Tax)
                                  </template>
                                  <template v-else
                                    >Revised Unit Cost (ex Tax)
                                  </template>
                                </th>

                                <th
                                  v-if="!isUSCountry"
                                  class="text-center pb-3 text-uppercase"
                                >
                                  <template v-if="isVariableWeight(item)"
                                    >Revised Cost / KG (inc Tax)
                                  </template>
                                  <template v-else
                                    >Revised Unit Cost (inc Tax)
                                  </template>
                                </th>

                                <th
                                  v-if="!isServiceProduct(item) && !isUSCountry"
                                  class="text-center pb-3 text-uppercase"
                                >
                                  <template v-if="isVariableWeight(item)"
                                    >Price Drop RRP / KG (inc Tax)</template
                                  >
                                  <template v-else
                                    >Price Drop Unit RRP (inc Tax)</template
                                  >
                                </th>

                                <th
                                  v-if="
                                    selectedStatus !== 'PAST' &&
                                    !isServiceProduct(item) &&
                                    !isUSCountry
                                  "
                                  class="text-center pb-3 text-uppercase"
                                >
                                  GP%
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr
                                :key="zone.code"
                                class="text-center"
                                v-for="zone in item.regionalPriceMap"
                              >
                                <td>{{ zone.displayName }}</td>
                                <td>
                                  {{ $filters.currency(zone.cartonPrice) }}
                                </td>
                                <td>{{ $filters.currency(zone.amountOff) }}</td>
                                <td class="i-td-chip">
                                  <v-chip class="chip" v-if="zone.amountOff">
                                    <template v-if="isVariableWeight(item)">{{
                                      $filters.currency(
                                        zone.revisedCostPerKGExTax
                                      )
                                    }}</template>
                                    <template v-else>{{
                                      $filters.currency(
                                        zone.revisedUnitPriceExTax
                                      )
                                    }}</template>
                                  </v-chip>
                                  <template v-else> - </template>
                                </td>
                                <td v-if="!isUSCountry" class="i-td-chip">
                                  <v-chip class="chip" v-if="zone.amountOff">
                                    <template v-if="isVariableWeight(item)">{{
                                      $filters.currency(
                                        zone.revisedCostPerKGIncTax
                                      )
                                    }}</template>
                                    <template v-else>{{
                                      $filters.currency(
                                        zone.revisedUnitPriceIncTax
                                      )
                                    }}</template>
                                  </v-chip>
                                  <template v-else> - </template>
                                </td>
                                <td
                                  v-if="!isServiceProduct(item) && !isUSCountry"
                                >
                                  {{ $filters.currency(zone.unitRrp) }}
                                </td>
                                <td
                                  v-if="
                                    selectedStatus !== 'PAST' &&
                                    !isServiceProduct(item) &&
                                    !isUSCountry
                                  "
                                  class="i-td-chip"
                                >
                                  <v-chip
                                    class="chip"
                                    :class="{
                                      negative: zone.grossProfitPercentage < 0,
                                      positive: zone.grossProfitPercentage > 0
                                    }"
                                    >{{
                                      zone.grossProfitPercentage || '-'
                                    }}</v-chip
                                  >
                                </td>
                              </tr>
                            </tbody>
                          </template>
                        </v-simple-table>

                        <span v-else class="text-center"
                          >No prices on known zones for this product</span
                        >
                      </td>
                    </template>
                  </v-data-table>
                </template>
              </template>

              <i-paginator
                v-if="pagination.totalPages > 1"
                class="mt-6"
                :currentPage="currentPage"
                :pageSize="pageSize"
                :pagination="pagination"
                @paginate="handlePageChange"
              />
            </v-col>
          </v-row>
        </template>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import _ from 'lodash'
import { mapActions, mapGetters, mapState } from 'vuex'
import SpecialsHelper from '@/modules/dataMaintenance/helper/SpecialsHelper'
import { deletePriceDrop, fetchPriceDrops } from '@/services/priceDrops'
import { findProductsForSupplierAndProductCodes } from '@/services/products'
import iConfirmationDialog from '@/components/ConfirmationDialog'
import iEditPriceDrops from '../components/EditPriceDrops'
import iLocalFilter from '@/components/LocalFilter'
import iOverlay from '@/components/Overlay'
import iInstructions from '@/components/Instructions'
import iEmptyResults from '@/components/EmptyResults'
import iPaginator from '@/components/Paginator'
import iTableRefreshButton from '../components/TableRefreshButton'
import { buildCountryDisplayRules } from '@/utils/countryDisplay'
import {
  buildUpcomingPriceChangeProductCodeSet,
  familyGroupHasUpcomingPriceChange,
  productHasUpcomingPriceChange,
  withUpcomingPriceChangeDisabled
} from '@/modules/dataMaintenance/helper/PromotionProductAvailabilityHelper'

const PROMOTIONS_PAGE_SIZE = 25

export default {
  name: 'price-drops',

  components: {
    iEmptyResults,
    iInstructions,
    iPaginator,
    iConfirmationDialog,
    iEditPriceDrops,
    iLocalFilter,
    iOverlay,
    iTableRefreshButton
  },

  props: {
    id: String,
    status: String
  },

  data: () => ({
    adding: false,
    addSpecialDialog: false,
    confirmationDialog: false,
    deletingSpecial: false,
    selectedSpecialId: null,
    editedItem: undefined,
    editSpecialDialog: false,
    headers: [
      {
        text: 'TARGET TYPE',
        value: 'targetType',
        width: '150',
        align: 'center',
        sortable: false
      },
      {
        text: 'TARGET(S)',
        value: 'targetValue',
        width: '20%',
        sortable: false
      },
      { text: 'TYPE', value: 'specialType', align: 'center', width: '80' },
      {
        text: 'SUPPLIER ITEM CODE',
        value: 'supplierItemCodes',
        align: 'center'
      },
      {
        text: 'FAMILY GROUP OR PRODUCT',
        value: 'displayName'
      },
      {
        text: 'START DATE',
        value: 'promotion.startDate',
        align: 'center',
        width: '140'
      },
      {
        text: 'END DATE',
        value: 'promotion.endDate',
        align: 'center',
        width: '180'
      },
      {
        text: 'ACTIONS',
        value: 'actions',
        sortable: false,
        align: 'center',
        width: '120'
      }
    ],
    headersAwaitingApproval: [
      {
        text: 'TARGET TYPE',
        value: 'targetType',
        width: '150',
        align: 'center',
        sortable: false
      },
      {
        text: 'TARGET(S)',
        value: 'targetValue',
        width: '20%',
        sortable: false
      },
      { text: 'TYPE', value: 'specialType', align: 'center', width: '80' },
      {
        text: 'SUPPLIER ITEM CODE',
        value: 'supplierItemCodes',
        align: 'center'
      },
      {
        text: 'FAMILY GROUP OR PRODUCT',
        value: 'displayName'
      },
      {
        text: 'START DATE',
        value: 'promotion.startDate',
        align: 'center',
        width: '140'
      },
      {
        text: 'END DATE',
        value: 'promotion.endDate',
        align: 'center',
        width: '180'
      },
      {
        text: 'STATUS',
        value: 'promotion.approvalStatus',
        align: 'center',
        width: '160'
      },
      {
        text: 'ACTIONS',
        value: 'actions',
        sortable: false,
        align: 'center',
        width: '120'
      }
    ],
    loading: false,
    currentPage: 1,
    pageSize: PROMOTIONS_PAGE_SIZE,
    pagination: {
      total: 0,
      totalPages: 0
    },
    isInitialLoadComplete: false,
    pendingFocusId: null,
    promotionLoadId: 0,
    pageProductsByCode: {},
    specials: [],
    filterText: '',
    selectedStatus: 'ACTIVE',
    specialDialog: false,
    editableSpecial: {},
    expandedSpecial: []
  }),

  computed: {
    ...mapState('products', ['products']),
    ...mapState('preferences', ['selectedSupplierId']),
    ...mapState('familyGroups', ['familyGroups']),
    ...mapState('pricingRegions', ['pricingRegions']),
    ...mapState('bannerGroups', ['bannerGroups']),
    ...mapState('multiStoreOperators', ['multiStoreOperators']),
    ...mapState('stores', ['retailerStores']),
    ...mapState('dataMaintenance', ['promotionUpdatedEvent']),
    ...mapGetters('dataMaintenance', ['priceDropsRequiringAmendmentsCount']),
    ...mapGetters('profile', ['dataApprovalRequired']),
    ...mapGetters('tenant', ['isILR', 'tenantCountryRegion']),

    headersByStatus() {
      return this.selectedStatus === 'AWAITING_APPROVAL'
        ? this.headersAwaitingApproval
        : this.headers
    },

    displayRules() {
      return buildCountryDisplayRules(this.tenantCountryRegion)
    },

    isUSCountry() {
      return this.displayRules.isUSCountry
    },

    emptyFilteredResultsText() {
      return `No ${this.selectedStatus.replaceAll('_', ' ')} Price Drops found`
    },

    upcomingPriceChangeProductCodes() {
      return buildUpcomingPriceChangeProductCodeSet(
        this.products,
        this.familyGroups
      )
    },

    enrichedProducts() {
      return this.products
        .filter((p) => p.status === 'LIVE')
        .map((p) => ({
          text: this.productDisplayName(p),
          value: p
        }))
        .map((option) =>
          withUpcomingPriceChangeDisabled(
            option,
            productHasUpcomingPriceChange(
              option.value,
              this.familyGroups,
              this.upcomingPriceChangeProductCodes
            )
          )
        )
        .sort((p1, p2) => (p1.text < p2.text ? -1 : 1))
    },

    enrichedFamilyGroups() {
      return this.familyGroups
        .filter((f) => f.productCodes.length)
        .map((f) => ({ text: f.name, value: f }))
        .map((option) =>
          withUpcomingPriceChangeDisabled(
            option,
            familyGroupHasUpcomingPriceChange(
              option.value,
              this.upcomingPriceChangeProductCodes
            )
          )
        )
        .sort((f1, f2) => (f1.text < f2.text ? -1 : 1))
    },

    enrichedSpecials() {
      return this.specials.map((promotion) => {
        if (promotion.familyGroup) {
          const familyGroup = this.familyGroups.find(
            (f) => f.id === promotion.familyGroup?.id
          )
          const familyProductCodes = (
            promotion.familyGroup?.products || []
          ).map((product) => product.productCode)
          const familyProducts = familyProductCodes
            .map((code) => this.getProductByCode(code))
            .filter(Boolean)

          let regionalPriceMap =
            SpecialsHelper.transformProductsToRegionalPriceMap(
              familyProducts,
              this.pricingRegions,
              this.tenantCountryRegion
            ).sort((a, b) => (a.code > b.code ? 1 : -1))

          regionalPriceMap = this.addPricesToRegionalPriceMap(
            regionalPriceMap,
            promotion.zonePrices
          )

          return {
            promotion,
            displayName: promotion.familyGroup?.name || '',
            familyGroupDeleted: promotion.familyGroup.deleted,
            specialType: 'FAMILY_GROUP',
            familyGroupId: promotion.familyGroup.id,
            familyGroupProducts: this.getFamilyGroupProducts(
              promotion.familyGroup.products
            ),
            supplierItemCodes: (familyProductCodes.length
              ? familyProductCodes
              : familyGroup?.productCodes || []
            ).map((code) => this.getSupplierItemId(code)),
            regionalPriceMap
          }
        }

        const product = this.getProductByCode(promotion.productCode)

        let regionalPriceMap =
          SpecialsHelper.transformProductToRegionalPriceMap(
            product,
            this.pricingRegions,
            this.tenantCountryRegion
          ).sort((a, b) => (a.code > b.code ? 1 : -1))

        regionalPriceMap = this.addPricesToRegionalPriceMap(
          regionalPriceMap,
          promotion.zonePrices
        )

        return {
          promotion,
          product,
          displayName:
            product?.tradeItemDescription || promotion.productCode || '',
          familyGroupDeleted: false,
          specialType: 'SINGLE_PRODUCT',
          supplierItemId: product?.supplierItemId,
          supplierItemCodes: [this.getSupplierItemId(promotion.productCode)],
          regionalPriceMap
        }
      })
    },

    specialsByStatus() {
      return this.enrichedSpecials
    },

    filteredSpecialsByStatus() {
      return this.specialsByStatus
    }
  },

  watch: {
    selectedStatus() {
      this.pendingFocusId = null
      this.currentPage = 1
      this.expandedSpecial = []
      this.reload()
    },
    promotionUpdatedEvent(event) {
      if (event?.promotionType === 'PRICE_DROP') this.reload()
    }
  },

  methods: {
    ...mapActions('products', ['fetchProducts']),
    ...mapActions('notifications', ['addNotification']),
    ...mapActions('familyGroups', ['fetchFamilyGroups']),
    ...mapActions('pricingRegions', ['fetchPricingRegions']),
    ...mapActions('dataMaintenance', ['fetchApprovalStatusStatistics']),

    localFilter(filterText) {
      this.filterText = typeof filterText === 'string' ? filterText.trim() : ''
      if (!this.isInitialLoadComplete) return
      this.pendingFocusId = null
      this.currentPage = 1
      this.expandedSpecial = []
      this.reload()
    },

    close() {
      this.editSpecialDialog = false
      this.editedItem = undefined
    },

    async expandSpecial(id) {
      const special = this.enrichedSpecials.find((p) => p.promotion.id === +id)
      if (!special) return

      if (this.selectedStatus !== special.promotion.status) {
        const wasReady = this.isInitialLoadComplete
        this.isInitialLoadComplete = false
        this.pendingFocusId = +id
        this.selectedStatus = special.promotion.status || 'ACTIVE'
        await this.reload()
        this.isInitialLoadComplete = wasReady
      }

      const expandedSpecial = this.enrichedSpecials.find(
        (p) => p.promotion.id === +id
      )
      this.expandedSpecial = expandedSpecial ? [expandedSpecial] : []
    },

    startDeletion(itemId) {
      this.confirmationDialog = true
      this.selectedSpecialId = itemId
    },

    resetDeletion() {
      this.confirmationDialog = false
      this.selectedSpecialId = null
    },

    confirmDeletion() {
      this.deletePriceDrop(this.selectedSpecialId)
    },

    editSpecial(special = {}) {
      this.editableSpecial = _.cloneDeep(special)
      this.specialDialog = true
    },

    deletePriceDrop(specialId) {
      this.deletingSpecial = true
      this.addNotification({
        color: 'secondary',
        text: 'Deleting your Price Drop...'
      })

      deletePriceDrop(this.selectedSupplierId, specialId)
        .then(() => {
          this.reload()
          this.addNotification({
            color: 'secondary',
            text: 'Price Drop successfully deleted!'
          })
        })
        .catch(() => {
          this.addNotification({ color: 'error', defaultError: true })
        })
        .finally(() => {
          this.deletingSpecial = false
          this.resetDeletion()
        })
    },

    getFamilyGroupProducts(products = []) {
      return products.map((product) => product.tradeItemDescription)
    },

    getProductByCode(productCode) {
      return (
        this.pageProductsByCode[productCode] ||
        this.products.find((p) => p.productCode === productCode)
      )
    },

    isVariableWeight(item) {
      return item.regionalPriceMap.find(
        (region) => region.variableWeightTargetInKg
      )
    },

    isServiceProduct(item) {
      if (item?.specialType !== 'SINGLE_PRODUCT') return false
      const productCode = item?.promotion?.productCode
      if (!productCode) return false
      const product = this.getProductByCode(productCode)
      return product?.productType === 'SERVICE'
    },

    async loadPageProducts(promotions) {
      const productCodes = [
        ...new Set(
          promotions.flatMap((promotion) => {
            if (promotion.familyGroup?.products?.length) {
              return promotion.familyGroup.products.map(
                (product) => product.productCode
              )
            }
            return promotion.productCode ? [promotion.productCode] : []
          })
        )
      ].filter(Boolean)

      if (!productCodes.length) {
        return {}
      }

      const products = await findProductsForSupplierAndProductCodes(
        this.selectedSupplierId,
        productCodes,
        { includeUpcoming: false }
      )

      return products.reduce((acc, product) => {
        acc[product.productCode] = product
        return acc
      }, {})
    },

    async getPriceDrops() {
      const promotionLoadId = ++this.promotionLoadId
      const pendingFocusId = this.pendingFocusId
      try {
        this.loading = true
        const response = await fetchPriceDrops(this.selectedSupplierId, {
          page: this.currentPage - 1,
          size: this.pageSize,
          query: this.filterText || undefined,
          status: pendingFocusId ? undefined : this.selectedStatus,
          focusId: pendingFocusId || undefined
        })
        const rawSpecials = Array.isArray(response)
          ? response
          : response?.promotionRules || []
        const pageProductsByCode = await this.loadPageProducts(rawSpecials)

        if (promotionLoadId !== this.promotionLoadId) return

        this.pagination = Array.isArray(response)
          ? {
              total: rawSpecials.length,
              totalPages: rawSpecials.length ? 1 : 0
            }
          : response?.pagination || { total: 0, totalPages: 0 }
        this.pendingFocusId = null
        this.pageProductsByCode = pageProductsByCode

        this.specials = rawSpecials.map((promotion) => {
          const enriched = { ...promotion }

          if (promotion.targetBannerGroups?.length) {
            enriched.targetBannerGroups = promotion.targetBannerGroups.map(
              (target) => {
                const match = this.bannerGroups.find(
                  (bg) => bg.id === target.bannerGroupId
                )
                return {
                  ...target,
                  displayName:
                    match?.displayName || `Banner ${target.bannerGroupId}`
                }
              }
            )
          }

          if (promotion.targetMultiStoreOperators?.length) {
            enriched.targetMultiStoreOperators =
              promotion.targetMultiStoreOperators.map((target) => {
                const match = this.multiStoreOperators.find(
                  (mso) => mso.id === target.multiStoreOperatorId
                )
                return {
                  ...target,
                  displayName:
                    match?.displayName || `MSO ${target.multiStoreOperatorId}`
                }
              })
          }

          if (promotion.targetStores?.length) {
            enriched.targetStores = promotion.targetStores.map((target) => {
              const match = this.retailerStores.find(
                (store) => store.id === target.storeId
              )
              return {
                ...target,
                displayName: `${target.storeName} ${
                  match?.pricingRegionExternalCode || ''
                }`.trim()
              }
            })
          }

          return enriched
        })
      } catch {
        if (promotionLoadId !== this.promotionLoadId) return
        this.addNotification({
          color: 'error',
          text: 'Price Drops data could not be loaded, please try again...'
        })
      } finally {
        if (promotionLoadId === this.promotionLoadId) this.loading = false
      }
    },

    addPricesToRegionalPriceMap(regionalPriceMap, prices) {
      return regionalPriceMap.map((r) => {
        const zone = prices.find((z) => z.pricingRegionCode === r.code)

        return {
          ...r,
          amountOff: zone?.amountOff || null,
          unitRrp: zone?.unitRrp || null,
          revisedUnitPriceExTax: zone?.revisedUnitPriceExTax || null,
          revisedUnitPriceIncTax: zone?.revisedUnitPriceIncTax || null,
          revisedCostPerKGExTax: zone?.revisedCostPerKGExTax || null,
          revisedCostPerKGIncTax: zone?.revisedCostPerKGIncTax || null,
          grossProfitPercentage: zone?.grossProfitPercentage || null
        }
      })
    },

    getLinkRoute(item) {
      return item.promotion.familyGroup
        ? {
            name: 'family-groups',
            query: { familyGroupId: item.familyGroupId }
          }
        : { name: 'products', query: { supplierItemId: item.supplierItemId } }
    },

    productDisplayName(p) {
      return `${p.supplierItemId} / ${p.tradeItemDescription}`
    },

    async reload() {
      await Promise.all([
        this.fetchApprovalStatusStatistics(),
        this.getPriceDrops()
      ])

      if (
        this.pagination.totalPages > 0 &&
        this.currentPage > this.pagination.totalPages
      ) {
        this.currentPage = this.pagination.totalPages
        return this.reload()
      }

      this.reset()
    },

    async handlePageChange(page) {
      this.currentPage = page
      this.expandedSpecial = []
      await this.reload()
    },

    async refreshCurrentTable() {
      await this.reload()
    },

    reset() {
      this.editableSpecial = {}
      this.specialDialog = false
    },

    getSupplierItemId(productCode) {
      const product = this.getProductByCode(productCode)
      return product ? product.supplierItemId : 'Unknown'
    }
  },

  async mounted() {
    try {
      this.loading = true
      this.fetchProducts({ supplierId: this.selectedSupplierId, all: true })
      this.fetchFamilyGroups(this.selectedSupplierId)
      this.fetchPricingRegions()
      this.selectedStatus = this.status?.toUpperCase() || 'ACTIVE'
      this.pendingFocusId = this.id ? Number(this.id) : null
      await this.reload()
      this.isInitialLoadComplete = true
      if (this.id) await this.expandSpecial(this.id)
    } catch {
      this.addNotification({
        color: 'error',
        text: 'Price Drops data could not be loaded, please try again...'
      })
    }
  }
}
</script>

<style lang="scss" scoped>
#price-drops {
  .promotion-table-actions__inner {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 42px;
    gap: 10px;
    align-items: stretch;
  }

  .v-btn:not(.v-btn--text):not(.v-btn--outlined):hover:before {
    opacity: 0;
  }
  .v-list-item {
    &.v-list-item--active {
      background-color: var(--v-secondary-base);
      color: white;
    }
  }
}

@media only screen and (min-width: 960px) {
  #price-drops {
    .v-data-table-header__icon {
      opacity: 1;
      margin-bottom: 3px;
    }
    .v-data-table-header__icon:hover {
      color: black;
    }
  }
}
</style>
